/**
 * Contact screening: finds a way to reach someone outside the platform in a
 * piece of member text (a mobile, a landline, a long run of digits, an email,
 * a link, a social handle, a messenger's name).
 *
 * The API refuses or withholds on a hit, and the forms run the same function
 * on blur so a member sees the rule before they submit. Positions in a hit are
 * positions in the raw text, so a desk can highlight exactly what was found.
 */

export type ContactHitKind =
  | 'MOBILE'
  | 'LANDLINE'
  | 'LONG_DIGITS'
  | 'EMAIL'
  | 'URL'
  | 'SOCIAL_HANDLE'
  | 'MESSENGER'

export interface ContactHit {
  kind: ContactHitKind
  raw: string
  normalized: string
  start: number
  end: number
}

export interface ContactScreen {
  clean: boolean
  hits: ContactHit[]
  kinds: ContactHitKind[]
  kindsFa: string[]
  redacted: string
}

export const CONTACT_SCREEN_FIELD_ERROR_FA = 'این فیلد نباید شماره تماس، ایمیل یا لینک داشته باشد.'
export const CONTACT_SCREEN_VALUE_ERROR_FA = 'مقدار نباید شماره تماس، ایمیل یا لینک داشته باشد.'

const KIND_FA: Record<ContactHitKind, string> = {
  MOBILE: 'شماره موبایل',
  LANDLINE: 'تلفن ثابت',
  LONG_DIGITS: 'شماره طولانی',
  EMAIL: 'ایمیل',
  URL: 'لینک',
  SOCIAL_HANDLE: 'آیدی شبکه اجتماعی',
  MESSENGER: 'نام پیام‌رسان',
}

export function contactKindFa(kind: ContactHitKind): string {
  return KIND_FA[kind] ?? kind
}

/** «شامل شماره موبایل، ایمیل»: the sentence a withheld message carries. */
export function contactReasonFa(kinds: readonly ContactHitKind[]): string {
  return `شامل ${kinds.map(contactKindFa).join('، ')}`
}

/* ------------------------------------------------------------------ */

interface Seg {
  ch: string
  start: number
  end: number
}

const UNITS: Record<string, number> = {
  صفر: 0, یک: 1, دو: 2, سه: 3, چهار: 4, پنج: 5, شش: 6, شیش: 6, هفت: 7, هشت: 8, نه: 9,
}
const TEENS: Record<string, number> = {
  ده: 10, یازده: 11, دوازده: 12, سیزده: 13, چهارده: 14, پانزده: 15, پونزده: 15,
  شانزده: 16, شونزده: 16, هفده: 17, هجده: 18, هیجده: 18, نوزده: 19,
}
const TENS: Record<string, number> = {
  بیست: 20, سی: 30, چهل: 40, پنجاه: 50, شصت: 60, هفتاد: 70, هشتاد: 80, نود: 90,
}
const HUNDREDS: Record<string, number> = {
  صد: 100, یکصد: 100, دویست: 200, سیصد: 300, چهارصد: 400, پانصد: 500, پونصد: 500,
  ششصد: 600, هفتصد: 700, هشتصد: 800, نهصد: 900,
}
const NUMBER_WORDS: Record<string, { value: number; level: number }> = {}
for (const [w, v] of Object.entries(UNITS)) NUMBER_WORDS[w] = { value: v, level: 1 }
for (const [w, v] of Object.entries(TEENS)) NUMBER_WORDS[w] = { value: v, level: 2 }
for (const [w, v] of Object.entries(TENS)) NUMBER_WORDS[w] = { value: v, level: 2 }
for (const [w, v] of Object.entries(HUNDREDS)) NUMBER_WORDS[w] = { value: v, level: 3 }

const WORD_ALT = Object.keys(NUMBER_WORDS)
  .sort((a, b) => b.length - a.length)
  .join('|')
const PHRASE = new RegExp(
  `(?<![\\p{L}\\p{M}])(?:${WORD_ALT})(?:\\s+و\\s+(?:${WORD_ALT}))*(?![\\p{L}\\p{M}])`,
  'gu',
)

/** «سیصد و سی و چهار» → ['334']; parts that do not descend start a new group. */
function phraseDigits(phrase: string): string {
  const parts = phrase.split(/\s+و\s+/)
  const groups: number[] = []
  let current: number | null = null
  let lastLevel = 99
  let unitTaken = false
  for (const part of parts) {
    const word = NUMBER_WORDS[part]
    if (!word) continue
    const teen = word.level === 2 && word.value < 20
    if (current !== null && word.level < lastLevel && !(unitTaken && word.level === 1)) {
      current += word.value
      lastLevel = teen ? 1 : word.level
      unitTaken = word.level === 1 || teen
    } else {
      if (current !== null) groups.push(current)
      current = word.value
      lastLevel = teen ? 1 : word.level
      unitTaken = word.level === 1 || teen
    }
  }
  if (current !== null) groups.push(current)
  return groups.map(String).join(' ')
}

function digitOf(ch: string): string | null {
  const code = ch.charCodeAt(0)
  if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0)
  if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660)
  return null
}

const isDigit = (ch: string | undefined): boolean => !!ch && ch >= '0' && ch <= '9'
const SEPARATORS = new Set([' ', '\u00a0', '-', '\u2010', '\u2011', '\u2013', '\u0640', '.', '(', ')', '\u066b'])

function normalise(text: string): Seg[] {
  // 1. Digits to Latin, ZWNJ dropped.
  const first: Seg[] = []
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '‌') continue
    first.push({ ch: digitOf(ch) ?? ch, start: i, end: i + 1 })
  }

  // 2. Number words to digits.
  const joined = first.map((s) => s.ch).join('')
  const second: Seg[] = []
  let cursor = 0
  PHRASE.lastIndex = 0
  for (const match of joined.matchAll(PHRASE)) {
    const at = match.index ?? 0
    for (let i = cursor; i < at; i++) second.push(first[i])
    const span = { start: first[at].start, end: first[at + match[0].length - 1].end }
    for (const ch of phraseDigits(match[0])) second.push({ ch, ...span })
    cursor = at + match[0].length
  }
  for (let i = cursor; i < first.length; i++) second.push(first[i])

  // 3. Dates keep their separators; between other digits separators go.
  const flat = second.map((s) => s.ch).join('')
  const keep = new Set<number>()
  for (const m of flat.matchAll(/1[34]\d{2}([-/])\d{1,2}\1\d{1,2}/g)) {
    for (let i = m.index ?? 0; i < (m.index ?? 0) + m[0].length; i++) keep.add(i)
  }
  const third: Seg[] = []
  for (let i = 0; i < second.length; i++) {
    const seg = second[i]
    if (SEPARATORS.has(seg.ch) && !keep.has(i)) {
      const prev = third[third.length - 1]
      let j = i + 1
      while (j < second.length && SEPARATORS.has(second[j].ch) && !keep.has(j)) j++
      const prevDigit = prev && isDigit(prev.ch)
      if (prevDigit && isDigit(second[j]?.ch)) {
        i = j - 1
        continue
      }
      // An opening parenthesis straight before a number is not part of it.
      if (seg.ch === '(' && isDigit(second[i + 1]?.ch)) continue
    }
    third.push(seg)
  }
  return third
}

const MESSENGERS = [
  'تلگرام', 'واتساپ', 'واتس اپ', 'ایتا', 'روبیکا', 'اینستاگرام', 'اینستا', 'سروش',
  'پیامرسان بله', 'در بله', 'بله پیام',
  'telegram', 'whatsapp', 'instagram', 'eitaa', 'rubika', 'soroush', 'signal',
]

const PATTERNS: Array<{ kind: ContactHitKind; re: RegExp }> = [
  { kind: 'EMAIL', re: /[A-Za-z0-9_.+-]+@[A-Za-z0-9-]+\.[A-Za-z0-9.-]+/g },
  {
    kind: 'URL',
    re: /(?:https?:\/\/\S+|www\.\S+|t\.me\/\S+|(?<![A-Za-z0-9@.-])[A-Za-z0-9-]+\.(?:ir|com|net|org|io|me|co)(?![A-Za-z0-9])(?:\/\S*)?)/gi,
  },
  { kind: 'SOCIAL_HANDLE', re: /(?<![A-Za-z0-9_@])@[A-Za-z0-9_.]{4,}/g },
  { kind: 'MOBILE', re: /(?<!\d)(?:\+?98|0098|0)?9\d{9}(?!\d)/g },
  { kind: 'LANDLINE', re: /(?<!\d)0[1-8]\d{8,9}(?!\d)/g },
  { kind: 'LONG_DIGITS', re: /(?<![\d,٬])\d{8,}(?![\d,٬])/g },
  {
    kind: 'MESSENGER',
    re: new RegExp(`(?<![\\p{L}\\p{M}])(?:${MESSENGERS.join('|')})(?![\\p{L}\\p{M}])`, 'giu'),
  },
]

export function screenContact(text: string | null | undefined): ContactScreen {
  const raw = text ?? ''
  if (!raw.trim()) return { clean: true, hits: [], kinds: [], kindsFa: [], redacted: raw }

  const segs = normalise(raw)
  const flat = segs.map((s) => s.ch).join('')
  const taken: Array<[number, number]> = []
  const hits: ContactHit[] = []

  for (const { kind, re } of PATTERNS) {
    re.lastIndex = 0
    for (const m of flat.matchAll(re)) {
      const a = m.index ?? 0
      const b = a + m[0].length
      if (taken.some(([x, y]) => a < y && b > x)) continue
      taken.push([a, b])
      const start = segs[a].start
      const end = segs[b - 1].end
      hits.push({ kind, raw: raw.slice(start, end), normalized: m[0], start, end })
    }
  }
  hits.sort((x, y) => x.start - y.start)

  const kinds: ContactHitKind[] = []
  for (const hit of hits) if (!kinds.includes(hit.kind)) kinds.push(hit.kind)

  let redacted = ''
  let last = 0
  for (const hit of hits) {
    if (hit.start < last) continue
    redacted += raw.slice(last, hit.start) + '[حذف شده]'
    last = hit.end
  }
  redacted += raw.slice(last)

  return { clean: hits.length === 0, hits, kinds, kindsFa: kinds.map(contactKindFa), redacted }
}

/**
 * Form side of the screen: `CONTACT_SCREEN_FIELD_ERROR_FA` for each field of
 * `values` named in `keys` whose text carries contact details, else false.
 * Spread into a `useFormErrors` validate, so the sentence shows on blur and
 * again at submit, the same one the API answers with.
 */
export function contactFieldErrorsFa<K extends string>(
  values: object,
  keys: readonly K[],
): Record<K, string | false> {
  const out = {} as Record<K, string | false>
  for (const key of keys) {
    const value = (values as Record<string, unknown>)[key]
    out[key] = typeof value === 'string' && !screenContact(value).clean && CONTACT_SCREEN_FIELD_ERROR_FA
  }
  return out
}
